/**
 * Supabase side of the KYS forms (ADR 0033): report_kys_fr11 / report_kys_fr12 (SECURITY INVOKER, RLS
 * applies), their exports through export_report() (which writes the audit row), the FR-12 approval and the
 * FR-11 maintenance plan and records (RLS: fleet managers+ of the vehicle's site write).
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  CHECKLIST_ANSWERS,
  CHECKLIST_RESULTS,
  KYS_APPROVER_ROLES,
  MAINTENANCE_KINDS,
  MAINTENANCE_ROLES,
  POWER_TYPES,
  VEHICLE_TYPES,
  type ChecklistAnswer,
  type ChecklistResult,
  type ExportFormat,
  type KysApproverRole,
  type KysFr11Row,
  type KysFr12Form,
  type MaintenanceKind,
  type MaintenanceRecord,
  type MaintenanceRole,
  type PowerType,
  type ReportResult,
  type VehicleType,
} from "@fts/core";
import { toFormRef } from "./checklist-mappers";
import type { KysFr11Query, KysFr12Query } from "./reports";

type PgError = { message: string; code?: string };
type Res<T> = { data: T | null; error: PgError | null };
type Check = <T>(res: Res<T>) => T;

const pick = <T extends string>(list: readonly T[], v: unknown, fallback: T): T => ((list as readonly string[]).includes(String(v)) ? (v as T) : fallback);
const pickOrNull = <T extends string>(list: readonly T[], v: unknown): T | null => ((list as readonly string[]).includes(String(v)) ? (v as T) : null);

export type Fr11Json = {
  vehicle_id: string; fleet_no: string; type: string; power: string; make: string | null; model: string | null; serial_no: string | null;
  site_id: string; site_name: string | null; active: boolean; interval_months: number | null; responsible_role: string | null;
  last_periodic_on: string | null; last_inspection_on: string | null; last_repair_on: string | null; open_faults: number; open_critical: number;
};
export type Fr12Json = {
  submission_id: string; vehicle_id: string; fleet_no: string; type: string; make: string | null; model: string | null; site_name: string | null;
  form_code: string; form_revision: string; form_revision_date: string | null; form_title: string | null; form_title_en: string | null; form_family?: string | null;
  document_no: number | null; submitted_at: string; shift_name: string | null; operator_known: boolean; result: string;
  approved_at: string | null; approved_role: string | null;
  answers: { position: number; code: string; text_tr: string; text_en: string | null; critical: boolean; answer: string; note: string | null }[];
};

export const toFr11Row = (r: Fr11Json): KysFr11Row => ({
  vehicleId: r.vehicle_id,
  fleetNo: r.fleet_no,
  vehicleType: pick<VehicleType>(VEHICLE_TYPES, r.type, "other"),
  power: pick<PowerType>(POWER_TYPES, r.power, "electric"),
  make: r.make,
  model: r.model,
  serialNo: r.serial_no,
  siteId: r.site_id,
  siteName: r.site_name,
  active: r.active === true,
  intervalMonths: r.interval_months === null ? null : Number(r.interval_months),
  responsibleRole: pickOrNull<MaintenanceRole>(MAINTENANCE_ROLES, r.responsible_role),
  lastPeriodicOn: r.last_periodic_on,
  lastInspectionOn: r.last_inspection_on,
  lastRepairOn: r.last_repair_on,
  openFaults: Number(r.open_faults) || 0,
  openCritical: Number(r.open_critical) || 0,
});

export const toFr12Form = (r: Fr12Json): KysFr12Form => ({
  submissionId: r.submission_id,
  vehicleId: r.vehicle_id,
  fleetNo: r.fleet_no,
  vehicleType: pick<VehicleType>(VEHICLE_TYPES, r.type, "other"),
  make: r.make,
  model: r.model,
  siteName: r.site_name,
  form: toFormRef(r) ?? { code: r.form_code, revision: r.form_revision, revisionDate: null, titleTr: "", titleEn: "" },
  documentNo: r.document_no === null ? null : Number(r.document_no),
  submittedAt: new Date(r.submitted_at).toISOString(),
  shiftName: r.shift_name,
  operatorKnown: r.operator_known === true,
  result: pick<ChecklistResult>(CHECKLIST_RESULTS, r.result, "ok"),
  approvedAt: r.approved_at ? new Date(r.approved_at).toISOString() : null,
  approvedRole: pickOrNull<KysApproverRole>(KYS_APPROVER_ROLES, r.approved_role),
  answers: (r.answers ?? []).map((a) => ({
    position: Number(a.position),
    code: a.code,
    textTr: a.text_tr,
    textEn: a.text_en ?? "",
    critical: a.critical === true,
    answer: pick<ChecklistAnswer>(CHECKLIST_ANSWERS, a.answer, "ok"),
    note: a.note,
  })),
});

async function tzOf(db: SupabaseClient, tenantId: string, siteId: string | null): Promise<string> {
  const res = await db.rpc("fts_report_tz", { p_tenant: tenantId, p_site: siteId });
  return typeof res.data === "string" ? res.data : "Europe/Istanbul";
}

/** What export_report() takes and logs for the KYS forms (ids and dates only). */
export function fr12Args(q: KysFr12Query): Record<string, unknown> {
  return q.submissionId
    ? { tenantId: q.tenantId, siteId: q.siteId, submissionId: q.submissionId }
    : { tenantId: q.tenantId, siteId: q.siteId, vehicleIds: q.vehicleIds, from: q.from, to: q.to };
}

export async function reportKysFr11(db: SupabaseClient, check: Check, q: KysFr11Query): Promise<ReportResult<KysFr11Row>> {
  const rows = check<Fr11Json[]>(await db.rpc("report_kys_fr11", { p_tenant: q.tenantId, p_site: q.siteId }));
  return { rows: (rows ?? []).map(toFr11Row), tz: await tzOf(db, q.tenantId, q.siteId), truncated: false };
}

export async function exportKysFr11(db: SupabaseClient, check: Check, q: KysFr11Query, format: ExportFormat): Promise<ReportResult<KysFr11Row>> {
  const rows = check<Fr11Json[]>(await db.rpc("export_report", { p_report: "kys_fr11", p_format: format, p_args: { tenantId: q.tenantId, siteId: q.siteId } }));
  return { rows: (rows ?? []).map(toFr11Row), tz: await tzOf(db, q.tenantId, q.siteId), truncated: false };
}

export async function reportKysFr12(db: SupabaseClient, check: Check, q: KysFr12Query): Promise<ReportResult<KysFr12Form>> {
  const rows = check<Fr12Json[]>(await db.rpc("report_kys_fr12", {
    p_tenant: q.tenantId, p_site: q.siteId, p_vehicles: q.vehicleIds.length ? q.vehicleIds : null,
    p_from: q.submissionId ? null : q.from, p_to: q.submissionId ? null : q.to, p_submission: q.submissionId ?? null,
  }));
  return { rows: (rows ?? []).map(toFr12Form), tz: await tzOf(db, q.tenantId, q.siteId), truncated: false };
}

export async function exportKysFr12(db: SupabaseClient, check: Check, q: KysFr12Query, format: ExportFormat): Promise<ReportResult<KysFr12Form>> {
  const rows = check<Fr12Json[]>(await db.rpc("export_report", { p_report: "kys_fr12", p_format: format, p_args: fr12Args(q) }));
  return { rows: (rows ?? []).map(toFr12Form), tz: await tzOf(db, q.tenantId, q.siteId), truncated: false };
}

export const RECORD_COLUMNS = "id, tenant_id, vehicle_id, kind, done_on, note, created_at, voids_record_id, void_reason";
export type RecordRow = {
  id: string; tenant_id: string; vehicle_id: string; kind: string; done_on: string; note: string | null; created_at: string;
  voids_record_id: string | null; void_reason: string | null;
};
/** Rows of one vehicle → records; `voided` = a later row of the list voids it. */
export function toRecords(rows: RecordRow[]): MaintenanceRecord[] {
  const voided = new Set(rows.map((r) => r.voids_record_id).filter(Boolean));
  return rows.map((r) => ({ ...toRecord(r), voided: voided.has(r.id) }));
}
export const toRecord = (r: RecordRow): MaintenanceRecord => ({
  id: r.id,
  tenantId: r.tenant_id,
  vehicleId: r.vehicle_id,
  kind: pick<MaintenanceKind>(MAINTENANCE_KINDS, r.kind, "periodic"),
  doneOn: r.done_on,
  note: r.note,
  createdAt: r.created_at,
  voidsRecordId: r.voids_record_id ?? null,
  voidReason: r.void_reason ?? null,
  voided: false,
});
